import { useQuery } from 'react-query';
import { awsApi } from '../services/api';
import { azureApi } from '../services/api';
import { hetznerApi } from '../services/api';
import { gcpApi } from '../services/api';
import { Link } from 'react-router-dom';
import { CloudCog, ChevronRight, Plus } from 'lucide-react';
import { clsx } from 'clsx';
import { Banner, Card, Chip, PageHeader, Skeleton, StatusBadge } from '@/components/ds-zta';
import { BRAND } from '@/brand';
import ProviderMark from '@/components/ProviderMark';

interface ProviderCard {
  id: string;
  name: string;
  /** Short monogram drawn on a neutral accent-tag tile (d2d is monochrome-violet: no brand fills). */
  logo: string;
  route: string;
  status: 'active' | 'coming_soon';
  accountCount?: number;
  /** The provider's account list is still on its first fetch. */
  loading?: boolean;
  resourceCount?: number;
}

export default function CloudProviders() {
  const { data: awsData, isLoading: awsLoading } = useQuery('aws-accounts', () => awsApi.listAccounts());
  const { data: azureData, isLoading: azureLoading } = useQuery('azure-subscriptions', () => azureApi.listSubscriptions());
  const { data: hetznerData, isLoading: hetznerLoading } = useQuery('hetzner-projects', () => hetznerApi.listProjects());
  const { data: gcpData, isLoading: gcpLoading } = useQuery('gcp-projects', () => gcpApi.listProjects());

  const awsAccounts = awsData?.data?.accounts || [];
  const azureSubs = azureData?.data?.subscriptions || [];
  const hetznerProjects = hetznerData?.data?.projects || [];
  const gcpProjects = gcpData?.data?.projects || [];

  const providers: ProviderCard[] = [
    {
      id: 'aws', name: 'Amazon Web Services', logo: 'AWS',
      route: '/cloud-providers/aws', status: 'active',
      accountCount: awsAccounts.length, loading: awsLoading,
    },
    {
      id: 'azure', name: 'Microsoft Azure', logo: 'AZ',
      route: '/cloud-providers/azure', status: 'active',
      accountCount: azureSubs.length, loading: azureLoading,
    },
    {
      id: 'hetzner', name: 'Hetzner Cloud', logo: 'HZ',
      route: '/cloud-providers/hetzner', status: 'active' as const,
      accountCount: hetznerProjects.length, loading: hetznerLoading,
    },
    {
      id: 'gcp', name: 'Google Cloud Platform', logo: 'GCP',
      route: '/cloud-providers/gcp', status: 'active' as const,
      accountCount: gcpProjects.length, loading: gcpLoading,
    },
    {
      id: 'oracle', name: 'Oracle Cloud', logo: 'OCI',
      route: '', status: 'coming_soon' as const,
    },
  ];

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <PageHeader
        className="mb-6"
        data-tour="cloud-header"
        icon={<CloudCog />}
        title="Cloud Providers"
        description="Manage cloud accounts and discover resources across providers"
      />

      {/* Provider Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-tour="cloud-providers">
        {providers.map((provider) => {
          const isActive = provider.status === 'active';
          const hasAccounts = (provider.accountCount || 0) > 0;

          return (
            <Card
              key={provider.id}
              data-tour={provider.id === 'aws' ? 'cloud-aws' : undefined}
              aria-disabled={isActive ? undefined : true}
              className={clsx(
                'relative group transition-colors',
                isActive
                  ? 'hover:border-[var(--ds-color-border-strong)] has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-[var(--ds-color-ring)]'
                  : 'opacity-60 bg-[var(--d2d-bg-secondary)]'
              )}
            >
              <div className="p-6">
                {/* Logo + Status */}
                <div className="flex items-start justify-between mb-4">
                  <ProviderMark label={provider.logo} size="lg" />
                  {/* No status chip for a supported provider: nothing reports
                      whether its accounts are live, and the count below says
                      what is configured (design eval F47). */}
                  {!isActive && (
                    <Chip variant="neutral" size="sm">Coming Soon</Chip>
                  )}
                </div>

                {/* Provider Name */}
                <h3 className={clsx(
                  'text-lg font-medium mb-1',
                  isActive ? 'text-[var(--d2d-text-primary)]' : 'text-[var(--d2d-text-muted)]'
                )}>
                  {provider.name}
                </h3>

                {/* Stats */}
                {isActive && (
                  <div className="text-sm text-[var(--d2d-text-muted)] mb-4">
                    {provider.loading
                      ? <Skeleton variant="text" width="45%" />
                      : hasAccounts
                      ? `${provider.accountCount} ${provider.id === 'azure' ? 'subscription' : provider.id === 'hetzner' ? 'project' : 'account'}${(provider.accountCount || 0) > 1 ? 's' : ''}`
                      : <StatusBadge value="inactive">Not configured</StatusBadge>}
                  </div>
                )}
                {!isActive && <div className="text-sm text-[var(--d2d-text-muted)] mb-4">Available in future release</div>}

                {/* Action — a real link, stretched over the whole card so the card stays one click target */}
                {isActive && (
                  <Link
                    to={provider.route}
                    className="flex items-center justify-between pt-3 border-t border-[var(--d2d-border)] text-[var(--brand)] focus-visible:outline-none after:absolute after:inset-0 after:content-['']"
                  >
                    <span className="text-sm font-semibold">
                      {hasAccounts ? 'Manage' : 'Get Started'}
                      <span className="sr-only"> {provider.name}</span>
                    </span>
                    {hasAccounts ? (
                      <ChevronRight aria-hidden className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    ) : (
                      <Plus aria-hidden className="h-4 w-4" />
                    )}
                  </Link>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Info Banner */}
      <Banner variant="info" title="Multi-Cloud Zero Trust" className="mt-8" data-tour="cloud-info">
        {BRAND.name} provides unified identity-based access control across all your cloud providers.
        Add an account, enable services, and discover resources — then control access through roles and access requests.
      </Banner>
    </div>
  );
}
